import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShoppingBag, Download, Truck, Coffee, Check, Copy,
  AlertCircle, Sparkles, Tag, ArrowLeft, ArrowRight, Loader2,
  Lock, Mail, FileText, Package,
} from "lucide-react";
import OGMeta from "@/components/OGMeta";
import { proxyAsset } from "@/lib/proxyAsset";
import "@/styles/spoils.css";

function fmt(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(Number(cents) % 100 === 0 ? 0 : 2)}`;
}

function ProductCard({ p }) {
  const isPWYW = p.pricing_mode === "pwyw";
  const isMerch = p.kind === "merch";
  const soldOut = isMerch && p.stock_remaining !== null && p.stock_remaining !== undefined && p.stock_remaining <= 0;
  return (
    <Link to={`/spoils/${p.slug}`} className="spoils-card no-underline" data-testid={`spoils-product-${p.slug}`}>
      <div className="cover">
        {soldOut && <div className="spoils-soldout"><span>Sold out</span></div>}
        {p.featured && <div className="ribbon gold">Featured</div>}
        {isPWYW && !p.featured && <div className="ribbon green">Pay what you want</div>}
        {p.cover_image_url ? (
          <img src={proxyAsset(p.cover_image_url)} alt={p.name} />
        ) : (
          <span className="placeholder">{isMerch ? "🎁" : "📁"}</span>
        )}
      </div>
      <div className="body">
        <div className="kind-tag flex items-center gap-1">
          {isMerch ? <Package size={10} /> : <FileText size={10} />}
          {p.kind === "digital" ? "Digital download" : "Merch"}
          {p.category ? ` · ${p.category}` : ""}
        </div>
        <div className="font-display uppercase text-lg mt-1 leading-tight text-[#F5F2E8]">{p.name}</div>
        {p.description && <p className="text-xs text-[#F5F2E8]/55 mt-1 line-clamp-2">{p.description.slice(0, 120)}</p>}
        <div className="mt-3 flex items-end justify-between gap-2">
          <div>
            {isPWYW ? (
              <>
                <div className="price pwyw">
                  {p.min_cents > 0 ? `From ${fmt(p.min_cents)}` : "Free / pay what you want"}
                </div>
                {p.suggested_cents > 0 && (
                  <div className="font-mono text-[10px] text-[#F5F2E8]/40 mt-0.5">
                    Suggested {fmt(p.suggested_cents)}
                  </div>
                )}
              </>
            ) : (
              <div className="price">{fmt(p.price_cents)}</div>
            )}
          </div>
          <ArrowRight size={16} className="text-[#D7A62A]" />
        </div>
      </div>
    </Link>
  );
}

export default function SpoilsPage() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get("/store")
      .then(({ data: d }) => setData(d))
      .catch(() => setData({ enabled: false, products: [], settings: {} }));
  }, []);

  const S = data?.settings || {};
  const products = useMemo(() => {
    let list = data?.products || [];
    if (cat) list = list.filter((p) => p.category === cat);
    return list;
  }, [data, cat]);

  if (!data) return <div className="p-10 font-mono text-sm text-[#F5F2E8]/55">Loading the spoils…</div>;

  if (!data.enabled) {
    return (
      <div className="py-12">
        <div className="spoils-closed-banner">
          <ShoppingBag size={36} className="mx-auto text-[#E53935]" />
          <h1 className="font-display uppercase text-4xl tracking-tight mt-3">
            {S.empty_state_title || "The Spoils is loading."}
          </h1>
          <p className="text-[#F5F2E8]/65 mt-2 max-w-md mx-auto whitespace-pre-line">
            {S.empty_state_body || "Restocking the shelves. Check back soon."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="spoils-page" data-testid="spoils-page">
      <OGMeta title={`${S.store_name || "The Spoils"} · The Local Loot`} description={S.store_tagline} />

      {/* HERO */}
      <section className="spoils-hero">
        <div className="relative max-w-6xl mx-auto">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D7A62A]">
            {S.store_hero_eyebrow || "Take what you need · pay what feels right"}
          </div>
          <h1 className="font-display uppercase text-5xl md:text-7xl mt-3 leading-[0.95]">
            {S.store_name || "The Spoils"}
          </h1>
          <p className="text-[#F5F2E8]/75 mt-4 text-base md:text-lg leading-relaxed max-w-2xl">
            {S.store_hero_subtitle}
          </p>
        </div>
      </section>

      {/* FILTER PILLS */}
      {data.categories?.length > 0 && (
        <div className="px-4 py-5 flex flex-wrap gap-2 max-w-6xl mx-auto" data-testid="spoils-filters">
          <button
            onClick={() => setCat("")}
            className={`spoils-filter-pill ${cat === "" ? "is-active" : ""}`}
            data-testid="spoils-filter-all"
          >
            All ({data.products?.length || 0})
          </button>
          {data.categories.map((c) => {
            const n = (data.products || []).filter((p) => p.category === c).length;
            return (
              <button key={c} onClick={() => setCat(c)} className={`spoils-filter-pill ${cat === c ? "is-active" : ""}`} data-testid={`spoils-filter-${c}`}>
                {c} ({n})
              </button>
            );
          })}
        </div>
      )}

      {/* PRODUCT GRID */}
      <div className="px-4 max-w-6xl mx-auto pb-12">
        {products.length === 0 ? (
          <div className="loot-card-flat p-10 text-center mt-6">
            <Package size={36} className="mx-auto text-[#F5F2E8]/25" />
            <div className="font-display uppercase text-2xl mt-3">No spoils in this aisle yet.</div>
            <p className="text-[#F5F2E8]/55 mt-2 text-sm">{S.empty_state_body}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="spoils-grid">
            {products.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* =====================================================================
   Product detail / checkout page
   ===================================================================== */
export function SpoilsDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [amountDollars, setAmountDollars] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cashapp");
  const [email, setEmail] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [note, setNote] = useState("");
  const [ship, setShip] = useState({ name: "", address_1: "", address_2: "", city: "", state: "", zip: "", country: "US" });
  const [step, setStep] = useState("buy"); // buy | pay
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/store/p/${slug}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData("notfound"));
  }, [slug]);

  // Initialize default amount once product loads. Guard inside the effect so
  // it sits above all early returns (React Hooks rules).
  useEffect(() => {
    if (!data || data === "notfound" || !data.product) return;
    const prod = data.product;
    if (prod.pricing_mode === "pwyw") {
      setAmountDollars(((prod.suggested_cents || prod.min_cents) / 100).toFixed(2));
    } else {
      setAmountDollars((prod.price_cents / 100).toFixed(2));
    }
  }, [data]);

  if (!data) return <div className="p-10 font-mono text-sm text-[#F5F2E8]/55">Loading…</div>;
  if (data === "notfound") {
    return (
      <div className="p-10 text-center">
        <h1 className="font-display uppercase text-3xl">Spoils not found.</h1>
        <Link to="/spoils" className="btn-loot inline-flex items-center gap-2 mt-4"><ArrowLeft size={14} /> Back to The Spoils</Link>
      </div>
    );
  }
  const p = data.product;
  const S = data.settings || {};
  const isPWYW = p.pricing_mode === "pwyw";
  const isMerch = p.kind === "merch";
  const soldOut = isMerch && p.stock_remaining !== null && p.stock_remaining !== undefined && p.stock_remaining <= 0;

  const amountCents = Math.round(Number(amountDollars || 0) * 100);
  const minCents = isPWYW ? (p.min_cents || 0) : (p.price_cents || 0);
  const minOk = amountCents >= minCents;

  const submit = async () => {
    if (!email.trim()) return toast.error("Email is required so we can send you the goods.");
    if (!minOk) return toast.error(`Minimum is ${fmt(minCents)}.`);
    if (isMerch && (!ship.name || !ship.address_1 || !ship.city || !ship.state || !ship.zip)) {
      return toast.error("Shipping address is required for merch.");
    }
    setBusy(true);
    try {
      const { data: d } = await api.post(`/store/p/${slug}/checkout`, {
        amount_cents: amountCents,
        payment_method: paymentMethod,
        buyer_email: email.trim(),
        buyer_name: buyerName.trim(),
        note: note.trim(),
        ship_name: ship.name, ship_address_1: ship.address_1, ship_address_2: ship.address_2,
        ship_city: ship.city, ship_state: ship.state, ship_zip: ship.zip, ship_country: ship.country,
      });
      setOrder(d);
      setStep("pay");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed.");
    } finally { setBusy(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied!"); };

  // PAYMENT INSTRUCTIONS SCREEN
  if (step === "pay" && order) {
    return <PaymentScreen p={p} S={S} order={order} onBack={() => setStep("buy")} navigate={navigate} />;
  }

  return (
    <div className="py-8 max-w-5xl mx-auto px-4" data-testid="spoils-detail">
      <Link to="/spoils" className="font-mono text-xs uppercase tracking-widest text-[#F5F2E8]/55 hover:text-[#D7A62A] inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to {S.store_name || "The Spoils"}
      </Link>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="spoils-detail-cover">
            {p.cover_image_url ? (
              <img src={proxyAsset(p.cover_image_url)} alt={p.name} />
            ) : (
              <span className="placeholder">{isMerch ? "🎁" : "📁"}</span>
            )}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#D7A62A] flex items-center gap-1">
            {isMerch ? <Package size={10} /> : <FileText size={10} />}
            {isMerch ? "Merch" : "Digital download"}
            {p.category && ` · ${p.category}`}
          </div>
          <h1 className="font-display uppercase text-4xl md:text-5xl mt-2 leading-[0.95]">{p.name}</h1>

          {!isPWYW && (
            <div className="font-display text-3xl text-[#D7A62A] mt-3">{fmt(p.price_cents)}</div>
          )}

          {p.description && (
            <div className="text-[#F5F2E8]/80 mt-4 whitespace-pre-line leading-relaxed text-[15px]">{p.description}</div>
          )}

          {p.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {p.tags.map((t) => (
                <span key={t} className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#F5F2E8]/15 text-[#F5F2E8]/55">
                  <Tag size={9} className="inline mr-1" />{t}
                </span>
              ))}
            </div>
          )}

          {soldOut && (
            <div className="mt-6 p-4 border border-[#E53935] bg-[#E53935]/10 text-[#E53935] font-display uppercase tracking-widest text-center">
              SOLD OUT
            </div>
          )}

          {!soldOut && (
            <div className="mt-6 space-y-4">
              {isPWYW && (
                <div className="spoils-pwyw">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#76B82A] mb-2">
                    Pay what you want{p.min_cents > 0 ? ` · minimum ${fmt(p.min_cents)}` : ""}
                  </div>
                  <input
                    type="number"
                    min={p.min_cents / 100} step="0.50"
                    value={amountDollars}
                    onChange={(e) => setAmountDollars(e.target.value)}
                    data-testid="spoils-amount-input"
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[p.min_cents, p.suggested_cents, p.suggested_cents * 2, p.suggested_cents * 3]
                      .filter((c, i, arr) => c > 0 && arr.indexOf(c) === i)
                      .map((c) => (
                        <button key={c} className="preset-pill" onClick={() => setAmountDollars((c / 100).toFixed(2))}>
                          {fmt(c)}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <input
                className="loot-input"
                placeholder="Your email (for delivery) *"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="spoils-email"
              />
              <input
                className="loot-input"
                placeholder="Your name (optional)"
                value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)}
              />

              {isMerch && (
                <div className="space-y-2 p-3 border border-[#F5F2E8]/15">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#76B82A] mb-1">
                    <Truck size={11} className="inline mr-1" /> Shipping address
                  </div>
                  <input className="loot-input" placeholder="Full name *" value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })} data-testid="spoils-ship-name" />
                  <input className="loot-input" placeholder="Street address *" value={ship.address_1} onChange={(e) => setShip({ ...ship, address_1: e.target.value })} />
                  <input className="loot-input" placeholder="Apt / Unit (optional)" value={ship.address_2} onChange={(e) => setShip({ ...ship, address_2: e.target.value })} />
                  <div className="grid grid-cols-3 gap-2">
                    <input className="loot-input" placeholder="City *" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} />
                    <input className="loot-input" placeholder="State *" value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value })} maxLength="3" />
                    <input className="loot-input" placeholder="ZIP *" value={ship.zip} onChange={(e) => setShip({ ...ship, zip: e.target.value })} maxLength="10" />
                  </div>
                </div>
              )}

              {/* Payment method tabs */}
              <div className="space-y-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/65">Payment method</div>
                <div className="flex flex-wrap gap-2">
                  {S.cashapp_handle && (
                    <button onClick={() => setPaymentMethod("cashapp")} className={`spoils-method-tab ${paymentMethod === "cashapp" ? "is-active" : ""}`} data-testid="spoils-method-cashapp">
                      💵 Cash App
                    </button>
                  )}
                  {S.venmo_handle && (
                    <button onClick={() => setPaymentMethod("venmo")} className={`spoils-method-tab ${paymentMethod === "venmo" ? "is-active" : ""}`} data-testid="spoils-method-venmo">
                      📱 Venmo
                    </button>
                  )}
                  {S.stripe_enabled && (
                    <button onClick={() => setPaymentMethod("stripe")} className={`spoils-method-tab ${paymentMethod === "stripe" ? "is-active" : ""}`} data-testid="spoils-method-stripe">
                      💳 Card (Stripe)
                    </button>
                  )}
                </div>
              </div>

              <textarea
                rows={2}
                className="loot-input"
                placeholder="Note to me (optional) — anything you want me to know about this purchase"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

              <button
                onClick={submit}
                disabled={busy || !minOk || !email.trim()}
                className="btn-loot w-full inline-flex items-center justify-center gap-2 text-base py-3"
                data-testid="spoils-checkout-btn"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ShoppingBag size={16} />}
                Continue · {fmt(amountCents)}
              </button>

              <p className="text-[10px] font-mono uppercase tracking-widest text-[#F5F2E8]/45 text-center">
                {isPWYW && p.kind === "digital" ? "Honor system · download is instant once you mark paid." : "Admin verifies payment, then your delivery is unlocked."}
              </p>
            </div>
          )}
        </div>
      </div>

      {S.tax_note && (
        <div className="mt-12 text-center text-xs text-[#F5F2E8]/45 font-mono whitespace-pre-line">
          {S.tax_note}
        </div>
      )}
    </div>
  );
}

function PaymentScreen({ p, S, order, onBack, navigate }) {
  const isPWYW = p.pricing_mode === "pwyw";
  const isDigital = p.kind === "digital";
  const canSelfConfirm = order.can_self_confirm;
  const method = order.payment_method;
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handle = method === "venmo" ? order.venmo_handle : order.cashapp_handle;
  const handleLink = method === "venmo"
    ? `https://venmo.com/${(order.venmo_handle || "").replace(/^@/, "")}?txn=pay&amount=${(order.amount_cents/100).toFixed(2)}&note=${encodeURIComponent(p.name)}`
    : `https://cash.app/${(order.cashapp_handle || "").replace(/^\$/, "")}/${(order.amount_cents/100).toFixed(2)}`;
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied!"); };

  const confirmPaid = async () => {
    setConfirming(true);
    try {
      await api.post(`/store/orders/${order.token}/mark-paid`);
      setConfirmed(true);
      toast.success("Marked paid — your download is ready!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't confirm");
    } finally { setConfirming(false); }
  };

  return (
    <div className="py-8 max-w-2xl mx-auto px-4">
      <button onClick={onBack} className="font-mono text-xs uppercase tracking-widest text-[#F5F2E8]/55 hover:text-[#D7A62A] inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back
      </button>

      <div className="spoils-pay-card">
        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D7A62A]">
          Step 1 of 2 · Send payment
        </div>
        <h1 className="font-display uppercase text-3xl md:text-4xl mt-2 leading-tight">
          Pay <span className="text-[#D7A62A]">${(order.amount_cents/100).toFixed(2)}</span> via {method === "venmo" ? "Venmo" : "Cash App"}
        </h1>
        <p className="text-[#F5F2E8]/75 mt-2">
          Send the exact amount to the handle below. Add <strong className="text-[#D7A62A]">"{p.name}"</strong> in the memo so I can match it up.
        </p>

        <div className="mt-5 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/55 mb-1">
                {method === "venmo" ? "Venmo handle" : "Cash App $cashtag"}
              </div>
              <div className="spoils-pay-handle truncate" onClick={() => copy(handle)} data-testid="spoils-pay-handle">{handle}</div>
            </div>
            <button onClick={() => copy(handle)} className="btn-ghost text-xs inline-flex items-center gap-1">
              <Copy size={12} /> Copy
            </button>
            <a href={handleLink} target="_blank" rel="noopener noreferrer" className="btn-loot text-xs inline-flex items-center gap-1">
              Open {method === "venmo" ? "Venmo" : "Cash App"} ↗
            </a>
          </div>
        </div>

        <div className="mt-5 border-t border-[#D7A62A]/20 pt-4 space-y-2">
          <div className="flex items-start text-sm text-[#F5F2E8]/85">
            <div className="spoils-step-badge">1</div>
            <div>Tap "Open {method === "venmo" ? "Venmo" : "Cash App"}" or copy the handle and send <strong className="text-[#D7A62A]">${(order.amount_cents/100).toFixed(2)}</strong>.</div>
          </div>
          <div className="flex items-start text-sm text-[#F5F2E8]/85">
            <div className="spoils-step-badge">2</div>
            <div>Add <strong>"{p.name}"</strong> in the note so I can match the payment.</div>
          </div>
          <div className="flex items-start text-sm text-[#F5F2E8]/85">
            <div className="spoils-step-badge">3</div>
            <div>{canSelfConfirm
              ? <>Hit <strong className="text-[#76B82A]">"I sent it"</strong> below — your download unlocks instantly. (Honor system — I check these later.)</>
              : isDigital
                ? <>I'll verify within 24h. You'll get an email with your download link.</>
                : <>I'll verify within 24h and ship within 5 business days. You'll get tracking by email.</>
            }</div>
          </div>
        </div>
      </div>

      {/* Self-confirm button */}
      {canSelfConfirm && !confirmed && (
        <button
          onClick={confirmPaid}
          disabled={confirming}
          className="btn-loot w-full inline-flex items-center justify-center gap-2 mt-5 text-base py-3"
          data-testid="spoils-confirm-paid"
        >
          {confirming ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          I sent it · unlock my download
        </button>
      )}

      {confirmed && (
        <div className="mt-5 p-5 border-2" style={{ borderColor: "#76B82A", background: "rgba(118,184,42,0.06)" }} data-testid="spoils-download-ready">
          <Sparkles size={20} className="text-[#76B82A]" />
          <h3 className="font-display uppercase text-2xl mt-2 text-[#76B82A]">You're in.</h3>
          <p className="text-[#F5F2E8]/85 mt-1">Your download is ready. Open it now — link lasts 10 uses.</p>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/store/orders/${order.token}/download`}
            target="_blank" rel="noopener noreferrer"
            className="btn-loot inline-flex items-center gap-2 mt-3"
            data-testid="spoils-download-now"
          >
            <Download size={14} /> Download now
          </a>
        </div>
      )}

      {!canSelfConfirm && (
        <div className="mt-5 p-4 border border-[#F5F2E8]/15 text-sm text-[#F5F2E8]/75">
          <Mail size={14} className="inline mr-1 text-[#D7A62A]" />
          Once paid, watch your inbox at <strong>{order.buyer_email || "your email"}</strong>. You can also bookmark this page to check status:
          <div className="font-mono text-[10px] mt-2 text-[#F5F2E8]/55 break-all">
            <Link to={`/spoils/order/${order.token}`} className="text-[#76B82A] underline">/spoils/order/{order.token?.slice(0, 14)}…</Link>
          </div>
        </div>
      )}

      <p className="text-center text-[#F5F2E8]/45 mt-6 text-xs">
        {S.thanks_message || "Thanks for supporting The Local Loot."}
      </p>
    </div>
  );
}

/* =====================================================================
   Order status page (bookmark-able)
   ===================================================================== */
export function SpoilsOrderPage() {
  const { token } = useParams();
  const [o, setO] = useState(null);

  const load = () => {
    api.get(`/store/orders/${token}`)
      .then(({ data }) => setO(data))
      .catch(() => setO("notfound"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (!o) return <div className="p-10 font-mono text-sm text-[#F5F2E8]/55">Loading order…</div>;
  if (o === "notfound") return <div className="p-10 text-center font-display uppercase">Order not found.</div>;

  const statusColor = {
    pending: "#D7A62A", paid: "#76B82A", shipped: "#76B82A",
    delivered: "#76B82A", cancelled: "#E53935",
  }[o.status] || "#F5F2E8";

  return (
    <div className="py-8 max-w-2xl mx-auto px-4" data-testid="spoils-order">
      <Link to="/spoils" className="font-mono text-xs uppercase tracking-widest text-[#F5F2E8]/55 hover:text-[#D7A62A] inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to The Spoils
      </Link>
      <div className="loot-card-flat p-6">
        <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: statusColor }}>Status</div>
        <div className="font-display uppercase text-3xl mt-1" style={{ color: statusColor }}>{o.status}</div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
          <div className="font-mono text-[10px] uppercase text-[#F5F2E8]/45">Product</div><div className="text-[#F5F2E8]">{o.product_name}</div>
          <div className="font-mono text-[10px] uppercase text-[#F5F2E8]/45">Amount</div><div className="text-[#D7A62A] font-display text-lg leading-none">{fmt(o.amount_cents)}</div>
          <div className="font-mono text-[10px] uppercase text-[#F5F2E8]/45">Method</div><div>{o.payment_method}</div>
          <div className="font-mono text-[10px] uppercase text-[#F5F2E8]/45">Placed</div><div className="font-mono text-xs">{o.created_at?.slice(0, 16)}</div>
          {o.tracking_number && (
            <>
              <div className="font-mono text-[10px] uppercase text-[#F5F2E8]/45">Tracking</div>
              <div className="font-mono text-xs">{o.tracking_number}</div>
            </>
          )}
        </div>

        {o.download_url && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/store/orders/${o.token}/download`}
            target="_blank" rel="noopener noreferrer"
            className="btn-loot inline-flex items-center gap-2 mt-5"
            data-testid="spoils-order-download"
          >
            <Download size={14} /> Download · {o.download_uses}/{o.download_max_uses} used
          </a>
        )}

        {o.status === "pending" && (
          <div className="mt-5 p-3 border border-[#D7A62A]/30 bg-[#D7A62A]/5 text-sm text-[#F5F2E8]/85 flex items-start gap-2">
            <AlertCircle size={14} className="text-[#D7A62A] mt-0.5" />
            <div>Waiting on verification. Check back soon or wait for the email.</div>
          </div>
        )}

        {o.admin_note && (
          <div className="mt-3 p-3 border-l-2 border-[#76B82A] bg-[#76B82A]/5 text-sm">
            <div className="font-mono text-[10px] uppercase text-[#76B82A] mb-1">Note from the founder</div>
            {o.admin_note}
          </div>
        )}
      </div>
    </div>
  );
}
